/** The project form's judgements (the NewProject board): the live name
 *  hint, the export name it previews, what is still missing, and what an
 *  edit has to send. The server has the last word on every one of them. */
import type { ProjectMemberIn, Station } from '../api/types'

export interface FormState {
  name: string
  client: string
  description: string
  timezone: 'Asia/Tbilisi' | 'UTC'
  crs_epsg: 32638 | 32637 | 4326
  stations: string[]
  members: ProjectMemberIn[]
}

const squash = (name: string) => name.split(/\s+/).filter(Boolean).join(' ').toLowerCase()

/** Another project the reader can see already uses this name, regardless
 *  of case and spacing (the server's unique index is the real check, and it
 *  also sees projects the reader cannot). */
export function nameTaken(name: string, projects: { id: number; name: string }[],
                          selfId?: number): boolean {
  const wanted = squash(name)
  return wanted !== '' && projects.some((p) => p.id !== selfId && squash(p.name) === wanted)
}

/** A project-named export, `{project} - {station} - {first day} to {last
 *  day}.{ext}`, without the characters Windows refuses in a file name. The
 *  server sanitises it again (`export.safe_filename`). */
export function projectExportName(project: string, station: string, firstDay: string,
                                  lastDay: string, ext = 'csv'): string {
  // Each name capped, so the dates always fit inside the server's 150.
  const clean = (text: string, max: number) =>
    text.replace(/[<>:"/\\|?*]/g, '').replace(/\s+/g, ' ').trim().slice(0, max).trim()
  return `${clean(project, 60)} - ${clean(station, 50)} - ${firstDay} to ${lastDay}.${ext}`
}

/** Why the form cannot be saved yet, in the board's words, or null. */
export function missingLine(name: string, stationCount: number, taken: boolean): string | null {
  if (taken) return 'Pick a name no other project uses.'
  if (name.trim() === '') return 'A name and at least one station are needed.'
  if (stationCount === 0) return 'Pick at least one station.'
  return null
}

export function isHdEmail(email: string): boolean {
  return /^[^@\s]+@hydrodiagnostics\.ge$/i.test(email.trim())
}

/** The accounts the People picker offers: active people (the server lists only
 *  those who have signed in and been approved) who are not already in the
 *  project, in the order the server gave them (by name). A member whose
 *  account has since gone pending or disabled is not in the server's list and
 *  stays where they are in the project's own list, to be lowered or removed. */
export function pickablePeople<P extends { email: string }>(people: readonly P[], members: readonly { email: string }[]): P[] {
  const taken = new Set(members.map((m) => m.email.trim().toLowerCase()))
  return people.filter((p) => !taken.has(p.email.trim().toLowerCase()))
}

/** The listed account for an address, or undefined: the picker only adds
 *  what the list holds. */
export function listedPerson<P extends { email: string }>(people: readonly P[], email: string): P | undefined {
  const wanted = email.trim().toLowerCase()
  return people.find((p) => p.email.trim().toLowerCase() === wanted)
}

/** What an edit sends: the basics that changed, and the whole station set
 *  or member list when either changed (the server replaces each set
 *  atomically and audits the difference). Null for a part left alone. */
export function formChanges(original: FormState, current: FormState) {
  const basics: Partial<FormState> = {}
  for (const key of ['name', 'client', 'description', 'timezone', 'crs_epsg'] as const) {
    if (original[key] !== current[key]) (basics as Record<string, unknown>)[key] = current[key]
  }
  const sameStations = [...original.stations].sort().join('\n') === [...current.stations].sort().join('\n')
  const roles = (list: ProjectMemberIn[]) =>
    list.map((m) => `${m.email.trim().toLowerCase()} ${m.role}`).sort().join('\n')
  return {
    basics: Object.keys(basics).length ? basics : null,
    stations: sameStations ? null : current.stations,
    members: roles(original.members) === roles(current.members) ? null : current.members,
  }
}

/** Where a failed save is said, and in what words: a name conflict under
 *  the name (whichever save met it), an account refusal under People, the
 *  rest at the foot. `part` is the edit's step that failed (null on create);
 *  `saved` the steps that went through before it, since each is its own
 *  request. */
const ACCOUNT_REFUSAL = new RegExp([
  'has not signed in yet', 'hydrodiagnostics\\.ge account', 'listed more than once',
  'waiting for approval', 'account is disabled', 'project Admin',
].join('|'))

export function saveFailure(part: string | null, status: number | undefined, text: string,
                            saved: string[]): { field: 'name' | 'email' | 'form'; text: string } {
  const already = saved.length ? ` Saved: ${saved.join(', ')}.` : ''
  if (status === 409) return { field: 'name', text: `${text}${already}` }
  const wrapped = part ? `The ${part} were not saved: ${text}${already}` : text
  return { field: ACCOUNT_REFUSAL.test(text) ? 'email' : 'form', text: wrapped }
}

/** The form on screen: the reader's own edits while they stay on the same
 *  project (`key`), whatever re-reads of the project bring, and the project
 *  as stored otherwise (null while it loads). */
export function heldForm(held: { key: string; form: FormState } | null, key: string,
                         original: FormState | null): FormState | null {
  return held?.key === key ? held.form : original
}

/** The stations the form offers: the list (active stations), and any of the
 *  project's own it no longer has, a decommissioned one, so it can still be
 *  unticked. */
export function pickableStations(listed: Station[], inProject: Station[]): Station[] {
  const codes = new Set(listed.map((s) => s.code))
  return [...listed, ...inProject.filter((s) => !codes.has(s.code))]
}
